import { useEffect, useRef, useState } from "react";
import SecureStreamPlayer, {
  type StreamPlayerHandle,
} from "../streaming/StreamPlayer";
import api from "../../lib/api";
import StreamStatusScreen, {
  StreamHeaderBar,
} from "../streaming/StreamStatusScreen";

interface Props {
  isLive: boolean;
  isPaused?: boolean;
  showResumedBanner?: boolean;
  isRecording?: boolean;
  streamPath: string;
  user: string;
  ip: string;
  caseId: string;
  courtRoomLabel?: string;
  courtName: string;
  mobile?: string;
  location?: string;
  cnr?: string;
}

export default function PublicViewerPlayer({
  isLive,
  isPaused,
  showResumedBanner,
  streamPath,
  user,
  isRecording,
  ip,
  cnr,
  courtRoomLabel,
  courtName,
  mobile,
  location,
}: Props) {
  const playerRef = useRef<StreamPlayerHandle>(null);
  const [hlsToken, setHlsToken] = useState<string>("");

  // imperative pause/play instead of unmount — mirrors UniversalPlayer
  useEffect(() => {
    if (!playerRef.current) return;
    if (isPaused) {
      playerRef.current.pause();
    } else if (isLive) {
      playerRef.current.play();
    }
  }, [isPaused, isLive]);

  useEffect(() => {

    const fetchToken = () => {

      api
        .get("/api/search/stream/public-hls-token")
        .then((res) => {

          setHlsToken(res.data.token);
        })
        .catch((error) => {
          console.error("[HLS Token] API error:", error);
        });
    };

    fetchToken();


    const interval = setInterval(
      () => {
        fetchToken();
      },
      30 * 60 * 1000,
    );

    return () => {
      clearInterval(interval);
    };
  }, []);

  // force play on a new source (switch), status can stay "live" across it
  useEffect(() => {
    if (!streamPath || !isLive || isPaused) return;
    const t = setTimeout(() => playerRef.current?.play(), 300);
    return () => clearTimeout(t);
  }, [streamPath, isLive, isPaused]);

  // Header is rendered once above the player, so overlays skip their own
  const statusScreenLabels = {
    courtLabel: courtRoomLabel || courtName || undefined,
    matterLabel: cnr ? `CNR: ${cnr}` : undefined,
  };

  const overlay = () => {
    if (isRecording && !isLive) {
      return (
        <StreamStatusScreen
          variant="overlay"
          status="notStreaming"
          {...statusScreenLabels}
          showHeader={false}
        />
      );
    }
    if (isPaused) {
      return (
        <StreamStatusScreen
          variant="overlay"
          status="paused"
          {...statusScreenLabels}
          showHeader={false}
        />
      );
    }
    if (!streamPath) {
      return (
        <StreamStatusScreen
          variant="overlay"
          status="notInSession"
          {...statusScreenLabels}
          showHeader={false}
        />
      );
    }
    if (!isLive) {
      return (
        <StreamStatusScreen
          variant="overlay"
          status="notStarted"
          {...statusScreenLabels}
          showHeader={false}
        />
      );
    }
    return null;
  };

  const currentOverlay = overlay();

  return (
    <div className="w-full h-full flex flex-col bg-black relative">
      <StreamHeaderBar {...statusScreenLabels} />
      <div className="relative flex-1 min-h-0 bg-black flex items-center justify-center overflow-hidden">
        {showResumedBanner && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[300] bg-green-600 text-white text-sm sm:text-base font-semibold px-4 py-2 rounded-full shadow-lg">
            Streaming has been resumed
          </div>
        )}

        {/* player always mounted — key stable so it never remounts */}
        <div className="flex h-full w-full items-center">
          <style>{`
            video {
              width: 100% !important;
              height: 99% !important;
              object-fit: contain !important;
              object-position: center center !important;
            }
          `}</style>
          <SecureStreamPlayer
            key="stable-player"
            ref={playerRef}
            streamPath={streamPath}
            hlsToken={hlsToken}
            user={user}
            ip={ip}
            caseId={cnr || "N/A"}
            courtName={statusScreenLabels.courtLabel || ""}
            mobile={mobile ?? ""}
            location={location}
            isPaused={isPaused}
            enableFullscreen={false}
            showStatusHeader={false}
          />
        </div>

        {currentOverlay && (
          <div className="absolute inset-0 z-20 bg-black">{currentOverlay}</div>
        )}
      </div>
    </div>
  );
}
